"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RetryInterceptor = void 0;
const axios_1 = require("axios");
const debug_logger_1 = require("../../../utils/debug-logger");
class RetryInterceptor {
    constructor() {
        this.debugLogger = new debug_logger_1.DebugLogger('RetryInterceptor');
        this.defaultOptions = Object.freeze({
            maxRetries: 3,
            retryDelay: 1000,
            backoffMultiplier: 2,
            maxRetryDelay: 30000,
            retryableStatusCodes: [
                axios_1.HttpStatusCode.RequestTimeout,
                axios_1.HttpStatusCode.TooManyRequests,
                axios_1.HttpStatusCode.InternalServerError,
                axios_1.HttpStatusCode.BadGateway,
                axios_1.HttpStatusCode.ServiceUnavailable,
                axios_1.HttpStatusCode.GatewayTimeout
            ],
            retryableErrorCodes: ['ECONNRESET', 'ENOTFOUND', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'],
            enableJitter: true
        });
        this.onRequest = async (config) => {
            if (!config._retryMetadata) {
                config._retryMetadata = {
                    attemptNumber: 0,
                    maxRetries: this.getRetryOptions(config.retry).maxRetries
                };
            }
            return config;
        };
        this.onError = async (error) => {
            var _a, _b;
            if (!((_a = error.config) === null || _a === void 0 ? void 0 : _a._retryMetadata)) {
                return Promise.reject(error);
            }
            const retryOptions = this.getRetryOptions(error.config.retry);
            const metadata = error.config._retryMetadata;
            metadata.attemptNumber++;
            error.config._retryMetadata = metadata;
            if (!this.shouldRetryError(error, metadata.attemptNumber, retryOptions)) {
                this.debugLogger.log(`Not retrying request to ${error.config.url} - max retries exceeded or error not retryable`);
                return Promise.reject(error);
            }
            const delay = this.calculateRetryDelay(metadata.attemptNumber, retryOptions);
            this.debugLogger.log(`Retrying request to ${error.config.url} - attempt ${metadata.attemptNumber}/${retryOptions.maxRetries} after ${delay}ms delay`);
            await ((_b = retryOptions.onRetry) === null || _b === void 0 ? void 0 : _b.call(retryOptions, error, metadata.attemptNumber, delay));
            await this.sleep(delay);
            try {
                const axiosInstance = new axios_1.Axios();
                error.config.adapter = axiosInstance.defaults.adapter;
                const response = await axiosInstance.request(error.config);
                this.debugLogger.log(`Request successful for ${error.config.url} on attempt ${metadata.attemptNumber}`);
                return response;
            }
            catch (retryError) {
                return this.onError(retryError);
            }
        };
    }
    runWhen(config) {
        return (config === null || config === void 0 ? void 0 : config.retry) !== false;
    }
    shouldRetryError(error, attemptNumber, options) {
        var _a;
        if (attemptNumber > options.maxRetries) {
            return false;
        }
        if (options.shouldRetry) {
            return options.shouldRetry(error, attemptNumber);
        }
        if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) {
            return options.retryableStatusCodes.includes(error.response.status);
        }
        if (error.code) {
            return options.retryableErrorCodes.includes(error.code);
        }
        if (error.message) {
            const retryableMessages = [
                'network error',
                'timeout',
                'connection reset',
                'socket hang up',
                'econnreset',
                'enotfound',
                'econnaborted',
                'etimedout'
            ];
            const lowerMessage = error.message.toLowerCase();
            return retryableMessages.some(msg => lowerMessage.includes(msg));
        }
        return false;
    }
    calculateRetryDelay(attemptNumber, options) {
        const baseDelay = options.retryDelay;
        const multiplier = options.backoffMultiplier;
        const maxDelay = options.maxRetryDelay;
        let delay = baseDelay * Math.pow(multiplier, attemptNumber - 1);
        delay = Math.min(delay, maxDelay);
        if (options.enableJitter) {
            const jitter = delay * 0.25 * (Math.random() * 2 - 1);
            delay = Math.max(0, delay + jitter);
        }
        return Math.round(delay);
    }
    getRetryOptions(retryConfig) {
        if (typeof retryConfig === 'boolean') {
            return this.defaultOptions;
        }
        return {
            ...this.defaultOptions,
            ...retryConfig
        };
    }
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
exports.RetryInterceptor = RetryInterceptor;
